// src/pages/Contacts.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { Table, Button, Space, Tag, Avatar, message, Input, Card, Popconfirm, Tooltip, Select, DatePicker } from 'antd';
import {
  PlusOutlined,
  ReloadOutlined,
  UserSwitchOutlined,
  UserOutlined,
  GlobalOutlined,
  DeleteOutlined,
  EditOutlined,
  MailOutlined,
  PhoneOutlined,
} from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';

import StandardPage from '../components/Layout/StandardPage';
import {
  listContacts,
  deleteContact,
  type ContactDTO,
} from '../services/contacts';

import CreateContactDrawer from '../components/contacts/CreateContactDrawer';
import EditContactDrawer from '../components/contacts/EditContactDrawer';
import ContactActivityDrawer from '../components/contacts/ContactActivityDrawer';
import ContactTimelineDrawer from '../components/contacts/ContactTimelineDrawer';
// ⬇️ Usa il nuovo drawer clienti (doppia funzione)
import CreateCustomerDrawer from '../components/customers/CreateCustomerDrawer';
import { listSectors, type SectorDTO } from '../services/sector';

const { Search } = Input;

const Contacts: React.FC = () => {
  const [data, setData] = useState<ContactDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);

  const [activityOpen, setActivityOpen] = useState(false);
  const [activityContactId, setActivityContactId] = useState<number | null>(null);

  const [timelineOpen, setTimelineOpen] = useState(false);
  const [timelineContact, setTimelineContact] = useState<ContactDTO | null>(null);

  const [editOpen, setEditOpen] = useState(false);
  const [editContact, setEditContact] = useState<ContactDTO | null>(null);

  // Drawer di conversione → CreateCustomerDrawer con contact precompilato
  const [convertOpen, setConvertOpen] = useState(false);
  const [convertContact, setConvertContact] = useState<ContactDTO | null>(null);

  const [searchText, setSearchText] = useState('');

  const [sectors, setSectors] = useState<SectorDTO[]>([]);
  const [sectorFilterId, setSectorFilterId] = useState<number | null>(null);

  // filtro per mese (createdAt)
  const [monthFilter, setMonthFilter] = useState<Dayjs | null>(null);

  const load = async () => {
    try {
      setLoading(true);
      const [items, sec] = await Promise.all([listContacts(), listSectors(true)]);
      setData(items || []);
      setSectors(Array.isArray(sec) ? sec : []);
    } catch (err: any) {
      message.error(err?.message || 'Errore durante il caricamento contatti');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filteredData = useMemo(() => {
    const src = Array.isArray(data) ? data : [];
    const q = searchText.trim().toLowerCase();
    let r = src;

    if (sectorFilterId != null) {
      r = r.filter(c => c.sector?.id === sectorFilterId);
    }
    if (monthFilter) {
      r = r.filter(c => c.createdAt && dayjs(c.createdAt).isSame(monthFilter, 'month'));
    }
    if (!q) return r;

    return r.filter((c) =>
      [
        c.businessName?.toLowerCase() || '',
        c.email?.toLowerCase() || '',
        c.phone?.toLowerCase() || '',
        c.website?.toLowerCase() || '',
      ].some((f) => f.includes(q))
    );
  }, [data, searchText, sectorFilterId, monthFilter]);

  const openActivityFor = (id: number) => {
    setActivityContactId(id);
    setActivityOpen(true);
  };
  const openTimelineFor = (contact: ContactDTO) => { setTimelineContact(contact); setTimelineOpen(true); };

  const columns = useMemo(
    () => [
      {
        title: 'Nome e settore',
        dataIndex: 'businessName',
        fixed: 'left' as const,
        width: 280,
        sorter: (a: ContactDTO, b: ContactDTO) =>
          (a.businessName || '').localeCompare(b.businessName || '', 'it', { sensitivity: 'base' }),
        render: (_: any, r: ContactDTO) => {
          const company = r.businessName || '—';
          return (
            <Space>
              <Avatar icon={<UserOutlined />} style={{ backgroundColor: r.isActive !== false ? '#1677ff' : '#ccc' }} />
              <div>
                <div style={{ fontWeight: 600 }}>{company}</div>
                {r.sector?.name && <div style={{ fontSize: 12, color: '#666' }}>{r.sector.name}</div>}
              </div>
            </Space>
          );
        },
      },
      {
        title: 'Data Creazione',
        dataIndex: 'createdAt',
        width: 160,
        sorter: (a: ContactDTO, b: ContactDTO) =>
          dayjs(a.createdAt || 0).valueOf() - dayjs(b.createdAt || 0).valueOf(),
        render: (v: string | undefined) =>
          v ? dayjs(v).format('DD/MM/YYYY HH:mm') : <span style={{ color: '#bbb' }}>—</span>,
      },
      {
        title: 'Ultima Attività',
        key: 'last',
        width: 190,
        sorter: (a: ContactDTO, b: ContactDTO) =>
          dayjs(a.lastActivityAt || 0).valueOf() - dayjs(b.lastActivityAt || 0).valueOf(),
        render: (_: any, r: ContactDTO) => {
          if (!r.lastActivityAt) return <span style={{ color: '#bbb' }}>—</span>;
          const when = dayjs(r.lastActivityAt).format('DD/MM/YYYY HH:mm');
          return <span style={{ fontSize: 12 }}>{when}</span>;
        },
      },
      {
        title: 'Cliente?',
        dataIndex: 'isCustomer',
        width: 180,
        sorter: (a: ContactDTO, b: ContactDTO) => {
          const abool = a.isCustomer ? 1 : 0;
          const bbool = b.isCustomer ? 1 : 0;
          if (abool !== bbool) return abool - bbool;
          const ad = dayjs((a as any).isCustomerSince || 0).valueOf();
          const bd = dayjs((b as any).isCustomerSince || 0).valueOf();
          return ad - bd;
        },
        render: (_: any, r: ContactDTO) => {
          const isCust = !!r.isCustomer;
          const sinceRaw = (r as any).isCustomerSince; // cambia se il campo ha un nome diverso
          const since = sinceRaw ? dayjs(sinceRaw).format('DD/MM/YYYY') : null;

          return (
            <Space direction="vertical" size={0}>
              {isCust ? <Tag color="green">Si</Tag> : <Tag>No</Tag>}
              {isCust && since && (
                <span style={{ fontSize: 12, color: '#666' }}>dal {since}</span>
              )}
            </Space>
          );
        },
      },
      {
        title: 'Contatti',
        key: 'contacts',
        width: 360,
        render: (_: any, r: ContactDTO) => {
          const parts: React.ReactNode[] = [];
          if (r.website) {
            const href = /^https?:\/\//i.test(r.website) ? r.website : `https://${r.website}`;
            parts.push(
              <a
                key="web"
                href={href}
                target="_blank"
                rel="noreferrer"
                onClick={(e) => e.stopPropagation()}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
              >
                <GlobalOutlined /> {r.website.replace(/^https?:\/\//i, '')}
              </a>
            );
          }
          if (r.phone) {
            parts.push(
              <span key="tel" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <PhoneOutlined /> {r.phone}
              </span>
            );
          }
          if (r.email) {
            parts.push(
              <a
                key="mail"
                href={`mailto:${r.email}`}
                onClick={(e) => e.stopPropagation()}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
              >
                <MailOutlined /> {r.email}
              </a>
            );
          }
          return parts.length ? <Space size={12} wrap>{parts}</Space> : <span style={{ color: '#bbb' }}>—</span>;
        },
      },
      {
        title: 'Azioni',
        key: 'actions',
        width: 420,
        render: (_: any, r: ContactDTO) => {
          const isCustomer = !!r.isCustomer;
          return (
            <Space wrap onClick={(e) => e.stopPropagation()}>
              <Tooltip title="Attività">
                <Button icon={<PlusOutlined />} onClick={() => {
                  setActivityContactId(r.id);
                  setActivityOpen(true);
                }}>Attività</Button>
              </Tooltip>
           
              <Tooltip title="Modifica contatto">
                <Button icon={<EditOutlined />} onClick={() => { setEditContact(r); setEditOpen(true); }}>Modifica</Button>
              </Tooltip>

              {/* Trasforma in cliente: disabilitato se già cliente, con tooltip dedicato */}
              <Tooltip title={isCustomer ? 'È già cliente' : 'Trasforma in cliente'}>
                <span>
                  <Button
                    type="primary"
                    icon={<UserSwitchOutlined />}
                    disabled={isCustomer}
                    onClick={() => { setConvertContact(r); setConvertOpen(true); }}
                  >
                    Trasforma
                  </Button>
                </span>
              </Tooltip>

              <Popconfirm
                title="Eliminare questo contatto?"
                okText="Elimina"
                cancelText="Annulla"
                okButtonProps={{ danger: true }}
                onConfirm={async () => {
                  try {
                    await deleteContact(r.id);
                    message.success('Contatto eliminato');
                    load();
                  } catch (err: any) {
                    message.error(err?.message || 'Errore durante eliminazione');
                  }
                }}
              >
                <Button danger icon={<DeleteOutlined />} />
              </Popconfirm>
            </Space>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data, sectorFilterId, monthFilter]
  );

  const actions = [
    { label: 'Nuovo Contatto', type: 'primary' as const, icon: <PlusOutlined />, onClick: () => setCreateOpen(true) },
    { label: 'Ricarica', type: 'default' as const, icon: <ReloadOutlined />, onClick: () => load() },
  ];

  return (
    <>
      <StandardPage title="Contatti" subtitle="Gestisci i prospect e le interazioni prima della conversione a cliente" actions={actions}>
        {/* Ricerca + Filtro Mese + Filtro Settore */ }
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
          <Search
            placeholder="Cerca per azienda, email, telefono o sito…"
            allowClear
            size="large"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ minWidth: 280, flex: 1 }}
            enterButton={false}
          />
          <DatePicker
            picker="month"
            allowClear
            placeholder="Filtra per mese di creazione"
            style={{ width: 200 }}
            value={monthFilter as any}
            onChange={(v) => setMonthFilter(v)}
          />
          <Select
            allowClear
            showSearch
            placeholder="Filtra per settore"
            style={{ width: 260 }}
            optionFilterProp="label"
            value={sectorFilterId ?? undefined}
            onChange={(val) => setSectorFilterId(val ?? null)}
            options={sectors.map(s => ({ label: s.name, value: s.id }))}
          />
          {(searchText || sectorFilterId != null || monthFilter) && (
            <span style={{ color: '#666', fontSize: 14, whiteSpace: 'nowrap' }}>
              {filteredData.length} di {data.length} contatti
            </span>
          )}
        </div>

        {/* Tabella */}
        <Card style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }} bodyStyle={{ padding: 0 }}>
          <Table<ContactDTO>
            rowKey="id"
            columns={columns as any}
            dataSource={filteredData}
            loading={loading}
            scroll={{ x: 1200 }}
            pagination={{
              style: { marginRight: 16 },
              total: filteredData.length,
              defaultPageSize: 8,
              pageSizeOptions: [8, 10, 20, 50, 100],
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) => `${range[0]}-${range[1]} di ${total} contatti`,
            }}
            onRow={(record) => ({
              onClick: () => openTimelineFor(record),
              style: { cursor: 'pointer' },
            })}
          />
        </Card>
      </StandardPage>

      {/* Crea contatto */}
      <CreateContactDrawer open={createOpen} onClose={() => setCreateOpen(false)} onCreated={load} />

      {/* Modifica contatto */}
      <EditContactDrawer open={editOpen} contact={editContact} onClose={() => setEditOpen(false)} onSaved={load} />

      {/* Attività */}
      <ContactActivityDrawer
        open={activityOpen}
        contactId={activityContactId}
        onClose={() => setActivityOpen(false)}
        onCreated={load}
      />

      {/* Timeline */}
      <ContactTimelineDrawer open={timelineOpen} contact={timelineContact} onClose={() => setTimelineOpen(false)} onRefresh={load} />

      {/* Conversione in cliente (prefill da contatto) */}
      <CreateCustomerDrawer
        open={convertOpen}
        onClose={() => setConvertOpen(false)}
        onCreated={load}
        contact={convertContact}
      />
    </>
  );
};

export default Contacts;
